import loadJS from 'fg-loadjs';
import Mustache from 'mustache/mustache.mjs';
import geodist from 'geodist';

const StoreLocator = {
  apiKey: 'AIzaSyCckTRE65sJQukdBtqFBEOhZdpPSLkp0Ek',
  stores: [],
  sidebarStoresId: 'sidebar-stores',
  inputFieldId: 'maps-autocomplete',
  map: null,
  mapMarkerPath: null,
  markers: {},
  infoWindow: null,
  isoCode: null,
  detailZoom: 17,

  loadWindowData() {
    this.stores = window.stores;
    this.isoCode = window.isoCode;
    this.mapMarkerPath = window.mapMarkerPath;
  },
  init() {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLatLng = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);
        this.showLocation(userLatLng);
      },
    );
    this.map = new google.maps.Map(
      document.getElementById('map'),
      {
        styles: require('./map-styles').default,
      }
    );

    const latLngBounds = new google.maps.LatLngBounds();
    this.stores.forEach((store) => {
      latLngBounds.extend(new google.maps.LatLng(store.lat, store.lng));
    });

    // Zoom in a little more than fitBounds to avoid too much marker-less space.
    if (this.shouldInitiallyZoomIn()) {
      google.maps.event.addListenerOnce(this.map, 'zoom_changed', () => {
        google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
          this.map.setZoom(this.map.getZoom() + 1);
        });
      });
    }

    this.map.setCenter(latLngBounds.getCenter());
    this.map.fitBounds(latLngBounds);
    this.updateStores();
    this.drawMarker();
    const autoComplete = new google.maps.places.Autocomplete(
      document.getElementById(this.inputFieldId),
      {
        bounds: latLngBounds,
        componentRestrictions: {
          country: this.isoCode,
        },
        fields: ['geometry'],
      }
    );

    autoComplete.addListener('place_changed', () => {
      const place = autoComplete.getPlace();
      this.map.setCenter(place.geometry.location);
      if (place.geometry.viewport) {
        this.map.fitBounds(place.geometry.viewport);
      } else {
        this.map.zoom(this.detailZoom);
      }
      this.updateStores();
    });
  },

  shouldInitiallyZoomIn() {
    return ['gb', 'de', 'nl', 'fr'].includes(this.isoCode);
  },

  showLocation(latLng) {
    this.map.setZoom(this.detailZoom);
    this.map.setCenter(latLng);
    this.updateStores();
  },

  updateStores() {
    this.updateStoreDistances();
    this.sortStoresByDistance();
    this.printSidebarStores();
  },

  sortStoresByDistance() {
    this.stores.sort((a, b) => {
      return a.distance - b.distance;
    });
  },

  addSidebarStoreEventListener() {
    this.stores.forEach((store) => {
      const storeElement = document.getElementById('store-' + store.uid);
      storeElement.addEventListener('click', function (e) {
        e.preventDefault();
        const uid = Number(this.dataset.uid);
        StoreLocator.openInfoWindow(uid);
      });
    });
  },

  openInfoWindow(uid) {
    this.closeInfoWindow();
    const store = this.getStoreByUid(uid);
    const html = this.renderInfoWindowTemplate(store);
    const marker = this.getMarker(uid);
    const infoWindow = new google.maps.InfoWindow({
      minWidth: 300,
    });
    infoWindow.setContent(html);
    infoWindow.open(this.map, marker);
    this.infoWindow = infoWindow;
    this.showLocation(marker.getPosition());
  },

  closeInfoWindow() {
    if (this.infoWindow) {
      this.infoWindow.close();
    }
  },

  getStoreByUid(uid) {
    return this.stores.filter((store) => {
      return store.uid === uid;
    })[0];
  },

  getMarker(uid) {
    return this.markers[uid];
  },

  updateStoreDistances() {
    const center = this.map.getCenter();
    this.stores.forEach((store) => {
      store.distance = geodist(
        {lat: store.lat, lon: store.lng},
        {lat: center.lat(), lon: center.lng()},
        {unit: 'km'}
      );
    });
  },

  drawMarker() {
    this.stores.forEach((store) => {
      let marker = new google.maps.Marker(
        {
          position: {
            lat: parseFloat(store.lat),
            lng: parseFloat(store.lng),
          },
          title: store.name,
          map: this.map,
          icon: this.mapMarkerPath,
        }
      );

      google.maps.event.addListener(
        marker,
        'click',
        () => {
          this.openInfoWindow(store.uid);
        }
      );

      this.markers[store.uid] = marker;
    });
  },

  renderInfoWindowTemplate(store) {
    return Mustache.render(this.getStoreInfoWindowTemplate(store), store);
  },

  printSidebarStores(noDistance = false) {
    const sidebarStoresContainer = document.getElementById(this.sidebarStoresId);
    if (noDistance) {
      sidebarStoresContainer.innerHTML = Mustache.render(this.getSidebarStoresTemplateNoDistance(), {stores: this.stores});
    } else {
      sidebarStoresContainer.innerHTML = Mustache.render(this.getSidebarStoresTemplate(), {stores: this.stores});
      this.addSidebarStoreEventListener();
    }
  },

  getSidebarStoresTemplateNoDistance() {
    return `
      {{#stores}}
      <li id="store-{{uid}}" data-uid="{{uid}}" class="p-1-5 border-bottom border-1 position-relative">
        <p class="mb-50 fs-sm"><strong><a href="#" class="link-before">{{name}}</a></strong></p>
        <div class="d-flex justify-content-between align-items-end smaller">
            <p class="m-0">{{address}}, <br>{{postcode}} {{city}}</p>
        </div>
      </li>
      {{/stores}}
    `;
  },

  getSidebarStoresTemplate() {
    return `
      {{#stores}}
      <li id="store-{{uid}}" data-uid="{{uid}}" class="p-1-5 border-bottom border-1 position-relative">
        <p class="mb-50 fs-sm"><strong><a href="#" class="link-before">{{name}}</a></strong></p>
        <div class="d-flex justify-content-between align-items-end smaller">
            <p class="m-0">{{address}}, <br>{{postcode}} {{city}}</p>
            <p class="m-0 text-muted"><i class="las la-map-marker me-25"></i>{{distance}} km</p>
        </div>
      </li>
      {{/stores}}
    `;
  },

  getStoreInfoWindowTemplate(store) {
    return `
      <div class="popover-map bg-white shadow-lg m-auto smaller">
        <div class="p-1-5 ">
            <div class="d-flex justify-content-between align-items-center">
                ` + this.getStoreTitleTemplate(store) + `
            </div>
              <p class="mb-50">{{address}}, <br>{{postcode}} {{city}}</p>
            <p class="m-0"><a href="tel:{{phone}}">{{phone}}</a></p>
        </div>
        <div class="border-top border-1 d-flex text-center">
            <div class="w-50 border-end border-1">
                <a href="https://maps.google.com/maps/dir//{{address}} {{postcode}} {{city}}" class="btn btn-link" target="_blank">Route</a>
            </div>
            {{#website_address}}
            <div class="w-50">
                <a href="{{website_address}}" target="_blank" class="btn btn-link">Website</a>
            </div>
            {{/website_address}}
        </div>
    </div>
    `;
  },

  getStoreTitleTemplate(store) {
    var template;
    if (store.website_address !== '') {
      template = '<p class="mb-50"><strong><a class="link-dark" href="{{website_address}}" target="_blank">{{name}}</a></strong></p>';
    } else {
      template = '<p class="mb-50"><strong>{{name}}</strong></p>';
    }

    return Mustache.render(template, store);
  },

  loadMaps() {
    loadJS('https://maps.googleapis.com/maps/api/js?callback=google_maps_init&libraries=places&key=' + this.apiKey);
  }
};

StoreLocator.loadWindowData();
StoreLocator.printSidebarStores(true);
document.querySelector('.accept-google-terms').addEventListener('click', function () {
  window.google_maps_init = StoreLocator.init.bind(StoreLocator);
  StoreLocator.loadMaps();
});
